import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import { delink } from 'bundles/third-party-auth/actions/AccountLinkActions';
import AccountLinkStore from 'bundles/third-party-auth/stores/AccountLinkStore';

import _t from 'i18n!nls/third-party-auth';

function getTranslations() {
  return {
    NO_PASSWORD_ERROR: _t('Please enter a valid password.'),
    GENERAL_ERROR: _t("Uh oh! We weren't able to delink your organization account. Please try again later."),
    SECTION_TITLE: _t('Organization account'),
    LINKED_MESSAGE: _t('Your Coursera account is linked to your organization account.'),
    SET_PASSWORD: _t('Please set a password for future logins to your Coursera account.'),
    DELINK_BUTTON: _t('Delink my organization account'),
  };
}

const NO_PASSWORD_SUPPLIED = 'noPasswordSupplied';

type Props = {
  linked: {
    saml: boolean;
    jwt: boolean;
  };
  requirePassword: boolean;
  errorCode?: string;
  successCode?: 'delink';
  loading: boolean;
};
type State = {};

class AccountLinkSSO extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof AccountLinkSSO.contextTypes>;

  declare _T: {
    NO_PASSWORD_ERROR: string;
    GENERAL_ERROR: string;
    SECTION_TITLE: string;
    LINKED_MESSAGE: string;
    SET_PASSWORD: string;
    DELINK_BUTTON: string;
  };

  constructor(props: Props, context: {}) {
    super(props, context);
    this._T = getTranslations();
  }

  handleDelink = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { accountType: this.props.linked.saml ? 'saml' : 'jwt', password: undefined as string | undefined };
    const password = this.passwordRef;
    if (password) {
      payload.password = password.value;
    }

    this.context.executeAction(delink, payload);
  };

  passwordRef: HTMLInputElement | null = null;

  renderErrorMessage() {
    if (this.props.errorCode) {
      return (
        <div role="alert" className="rc-StatusMessage error">
          {this.props.errorCode === NO_PASSWORD_SUPPLIED ? this._T.NO_PASSWORD_ERROR : this._T.GENERAL_ERROR}
        </div>
      );
    } else {
      return null;
    }
  }

  renderSuccessMessage() {
    const messages = {
      delink: _t('Successfully delinked your SSO account.'),
    };
    const { successCode } = this.props;
    const message = successCode && messages[successCode];
    if (message) {
      return (
        <div role="alert" className="rc-StatusMessage success">
          {message}
        </div>
      );
    } else {
      return null;
    }
  }

  renderButton(buttonText: string, trackingName: string, onClick?: () => void) {
    return (
      <TrackedCdsButton
        trackingName={trackingName}
        // eslint-disable-next-line camelcase
        trackingData={{ account_type: this.props.linked.saml ? 'saml' : 'jwt' }}
        variant="secondary"
        onClick={onClick}
        loading={this.props.loading}
        type="submit"
      >
        {buttonText}
      </TrackedCdsButton>
    );
  }

  render() {
    if (this.props.linked.saml || this.props.linked.jwt) {
      return (
        <div className="rc-LinkSSO account-link-option">
          <h5 className="account-link-option__header">{this._T.SECTION_TITLE}</h5>
          <div>
            <p>{this._T.LINKED_MESSAGE}</p>
            <form onSubmit={this.handleDelink}>
              {this.props.requirePassword && (
                <div className="settings-input-wrapper">
                  <label htmlFor="password">{this._T.SET_PASSWORD}</label>
                  <input
                    ref={(c) => {
                      this.passwordRef = c;
                    }}
                    id="password"
                    type="password"
                    required
                  />
                </div>
              )}
              <div>
                {this.renderButton(this._T.DELINK_BUTTON, 'delink_sso')}
                {this.renderSuccessMessage()}
                {this.renderErrorMessage()}
              </div>
            </form>
          </div>
        </div>
      );
    } else {
      return <span />;
    }
  }
}

export default connectToStores(
  AccountLinkSSO,
  // this should probably be `AccountLinkStore.storeName`
  // but I don't feel comfortable changing behavior
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  [AccountLinkStore],
  (storeInstances: { AccountLinkStore: AccountLinkStore }) => {
    return storeInstances[AccountLinkStore.storeName].dehydrate();
  }
);
